import React, { VFC, useContext, useCallback } from 'react';

import classNames from 'classnames';
import { IconButton, Text } from 'mintel-react-ui';

import ArticleBlock from '/components/ArticleBlock';
import { DangerousP, DangerousSpan } from '/components/common';
import { BlockProps } from '/types';
import { assetUri, Asset } from '/utils/assetUri';
import {
  MetadataContext,
  PresentModeContext,
  PublishStatusContext,
  FavoritesContext,
  UserAccessRestrictionContext,
} from '/utils/contexts';
import { contentFilter } from '/utils/functions';

import styles from './BlockHero.scss';
import useTranslate from '../../utils/translations';

const getArticleThumbnailUrl = (
  articleImageUrl: string | undefined,
  insightType: string | undefined,
): string => {
  if (articleImageUrl) {
    if (insightType === 'Innovative Product') {
      return `${articleImageUrl}/trimmed-jpg`;
    }
    return articleImageUrl;
  }
  return '';
};

const BlockHero: VFC<BlockProps> = ({ blockContent, ...props }) => {
  const {
    id: articleId,
    title: articleTitle,
    article_image_url: articleImageUrl,
    group_id: groupId,
    aggregated_insight_type: insightType,
    date: { display: dateString },
    editor,
    leaf_category: { name: categories } = { name: [] },
  } = useContext(MetadataContext);
  const t = useTranslate();
  const presentMode = useContext(PresentModeContext);
  const displayDate = dateString && new Date(dateString);
  const dateStamp =
    displayDate && `${displayDate.getFullYear()}-${displayDate.getMonth()}`;
  const formattedDate =
    displayDate &&
    displayDate.toLocaleString('default', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  const publishStatus = useContext(PublishStatusContext);
  const isLongTermPreview = publishStatus === 'long_term_preview';
  const accessRestrictions = useContext(UserAccessRestrictionContext);
  const presentEnabled = !!presentMode;
  const { isFavorited, onFavorite } = useContext(FavoritesContext);

  const subtitle = contentFilter(
    blockContent.text,
    presentMode,
    props.presentOption,
  );

  const favoriteHandler = useCallback(() => {
    const articleData = {
      id: articleId,
      title: articleTitle || '',
      url: window.location.href,
      article_image_url: getArticleThumbnailUrl(articleImageUrl, insightType),
      group_id: groupId,
    };
    onFavorite(articleData);
  }, [
    articleId,
    articleTitle,
    articleImageUrl,
    groupId,
    insightType,
    onFavorite,
  ]);

  return (
    <ArticleBlock {...props} offset={1} blockContent={blockContent} noTitle>
      <header
        className={classNames(styles['heading-container'], {
          [styles['present']]: presentEnabled,
        })}
      >
        {!presentEnabled && categories && categories.length > 0 && (
          <div className={styles['categories']}>
            <Text.Body>
              {categories.length > 2 ? 'Cross-category' : categories.join(', ')}
            </Text.Body>
          </div>
        )}
        <div className={styles['heading-body']}>
          <Text.Heading1>
            <DangerousSpan className={styles.title}>
              {blockContent?.title?.content}
            </DangerousSpan>
          </Text.Heading1>
          {!presentEnabled &&
            !accessRestrictions.isLibraryLockdownUser &&
            !accessRestrictions.isAnonymousUser &&
            !isLongTermPreview &&
            accessRestrictions.isLoggedInUser && (
              <div
                className={styles['favorite-star']}
                data-intercom-target="cpa-favourite"
              >
                <IconButton
                  name="favorites"
                  label={t('Add to favorites')}
                  tooltipProps={{ placement: 'bottom' }}
                  onClick={favoriteHandler}
                  size={36}
                  color={styles.star}
                  className={styles.starIcon}
                  alt_color={
                    isFavorited(articleId || '', groupId)
                      ? styles['star-fill']
                      : styles['mintel-white']
                  }
                  hoverColor={styles['star-hover']}
                />
              </div>
            )}
          <hr className={styles.underline} />
          {subtitle && (
            <Text.Body>
              <DangerousP className={styles.subtitle}>{subtitle}</DangerousP>
            </Text.Body>
          )}
          {'name' in editor && (
            <figure className={styles.editor}>
              <img
                alt={'Analyst image'}
                className={styles['editor-image']}
                src={
                  editor?.image_url ||
                  assetUri('a_mintel_analyst', Asset.Image, publishStatus)
                }
              />
              <figcaption>
                <Text.Body2>
                  {editor.name}, {editor?.job_title}
                </Text.Body2>
              </figcaption>
            </figure>
          )}
        </div>
        {!presentEnabled && (
          <div className={styles.footer}>
            <Text.Body2>
              <time className={styles.date} dateTime={dateStamp}>
                {formattedDate}
              </time>
            </Text.Body2>
            {insightType && (
              <Text.Body2>{insightType.toUpperCase()}</Text.Body2>
            )}
          </div>
        )}
      </header>
    </ArticleBlock>
  );
};

BlockHero.displayName = 'BlockHero';
export default BlockHero;
